import React, { useState, useEffect } from 'react';
import { usePIM } from '../../context/PIMContext';
import { useToast } from '../../context/ToastContext';
import { GhostButton } from '../ui/GhostButton';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Plus, Settings, Trash, Check, X, Pencil } from 'lucide-react';
import { ToDoGroupManagementModal } from '../modals/ToDoGroupManagementModal';
import { ToDoAddModal } from '../modals/ToDoAddModal';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { ConfirmationModal } from '../ui/ConfirmationModal';

export const ToDoListTab = () => {
    const { toast } = useToast();
    const { productData, charges, statuses } = usePIM();
    const [todos, setTodos] = useState([]);
    const [groups, setGroups] = useState([]);
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Edit State
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
        isDestructive: false
    });

    // Fetch Groups
    const fetchGroups = React.useCallback(async () => {
        try {
            const res = await fetch('/api/todo-groups', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (res.ok) setGroups(await res.json());
        } catch (err) { console.error("Error fetching groups:", err); }
    }, []);

    // Fetch Todos
    const fetchTodos = React.useCallback(async () => {
        if (!productData?.sku) return;
        try {
            const res = await fetch(`/api/products/${productData.sku}/todos`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (res.ok) setTodos(await res.json());
        } catch (err) { console.error("Error fetching todos:", err); }
    }, [productData?.sku]);

    useEffect(() => {
        fetchGroups();
        fetchTodos();
    }, [fetchGroups, fetchTodos]);

    // --- TODO HANDLERS ---
    const handleAddTodo = async (todoData) => {
        try {
            const res = await fetch(`/api/products/${productData.sku}/todos`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify(todoData)
            });
            if (res.ok) {
                fetchTodos();
            }
        } catch (err) { console.error("Error adding todo:", err); }
    };

    const startEditing = (todo) => {
        setEditingId(todo.id);
        setEditData({
            task: todo.task,
            group_id: todo.group_id || '',
            charge_id: todo.charge_id || '',
            status: todo.status,
            due_date: todo.due_date ? todo.due_date.split('T')[0] : '',
            notes: todo.notes || ''
        });
    };

    const saveEditing = async (id) => {
        try {
            const res = await fetch(`/api/todos/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({
                    ...editData,
                    group_id: editData.group_id || null,
                    charge_id: editData.charge_id || null,
                    due_date: editData.due_date || null
                })
            });
            if (res.ok) {
                fetchTodos();
                setEditingId(null);
            }
        } catch (err) { console.error("Error updating todo:", err); }
    };

    const handleDeleteTodo = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Task',
            message: 'Are you sure you want to delete this task?',
            isDestructive: true,
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/todos/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (res.ok) fetchTodos();
                } catch (err) { console.error("Error deleting todo:", err); }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    // --- GROUP HANDLERS ---
    const handleAddGroup = async (groupData) => {
        try {
            const res = await fetch('/api/todo-groups', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify(groupData)
            });
            if (res.ok) fetchGroups();
        } catch (err) { console.error("Error adding group:", err); }
    };

    const handleUpdateGroup = (groupData) => {
        // Implement modal or inline edit for group
        console.log("Update group:", groupData);
        toast.info("Update group not fully implemented in backend yet.");
    };

    const handleDeleteGroup = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Group',
            message: 'Delete this group? Tasks in this group will be unassigned.',
            isDestructive: true,
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/todo-groups/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (res.ok) fetchGroups();
                } catch (err) { console.error("Error deleting group:", err); }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    // Sorting
    const { items: sortedTodos, requestSort, sortConfig } = useSortableData(todos);

    const getStatusColor = (statusName) => {
        const status = statuses.find(s => s.name === statusName);
        return status ? status.hex_code : 'stone';
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">To Do List</h2>
                    <p className="text-xs text-stone-500 mt-1">Manage tasks for this product.</p>
                </div>
                <div className="flex gap-3">
                    <GhostButton onClick={() => setIsAddModalOpen(true)}><Plus size={16} /> Add Task</GhostButton>
                    <GhostButton onClick={() => setIsGroupModalOpen(true)}><Settings size={16} /> Manage Groups</GhostButton>
                </div>
            </div>

            {/* Todo List */}
            <div className="bg-stone-900/50 border border-stone-700 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10 bg-stone-950 text-xs text-stone-500 uppercase font-semibold">
                            <tr>
                                <SortableHeader label="Group" sortKey="group_name" currentSort={sortConfig} onSort={requestSort} className="w-[12%]" />
                                <SortableHeader label="Charge" sortKey="charge_name" currentSort={sortConfig} onSort={requestSort} className="w-[12%]" />
                                <SortableHeader label="Task" sortKey="task" currentSort={sortConfig} onSort={requestSort} className="w-[25%]" />
                                <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={requestSort} className="w-[12%]" />
                                <SortableHeader label="Due Date" sortKey="due_date" currentSort={sortConfig} onSort={requestSort} className="w-[12%]" />
                                <SortableHeader label="Notes" sortKey="notes" currentSort={sortConfig} onSort={requestSort} />
                                <th className="px-4 py-3 border-b border-stone-800 text-right w-[100px]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {sortedTodos.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center text-stone-500 italic">No tasks found.</td>
                                </tr>
                            )}
                            {sortedTodos.map(todo => {
                                const isEditing = editingId === todo.id;
                                return (
                                    <tr key={todo.id} className={`border-b border-stone-800 last:border-0 transition-colors ${isEditing ? 'bg-stone-800/50' : 'hover:bg-stone-800/30'}`}>
                                        {/* Group */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <select
                                                    value={editData.group_id}
                                                    onChange={(e) => setEditData({ ...editData, group_id: e.target.value })}
                                                    className="bg-stone-800 border-stone-600 text-white text-xs rounded px-2 h-8 w-full focus:outline-none focus:border-blue-500"
                                                >
                                                    <option value="">No Group</option>
                                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                                </select>
                                            ) : (
                                                todo.group_name ? <Badge color={todo.group_color || 'gray'}>{todo.group_name}</Badge> : <span className="text-stone-600">-</span>
                                            )}
                                        </td>

                                        {/* Charge */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <select
                                                    value={editData.charge_id}
                                                    onChange={(e) => setEditData({ ...editData, charge_id: e.target.value })}
                                                    className="bg-stone-800 border-stone-600 text-white text-xs rounded px-2 h-8 w-full focus:outline-none focus:border-blue-500"
                                                >
                                                    <option value="">All Charges</option>
                                                    {charges.map(c => <option key={c.id} value={c.id}>{c.batch_number || c.charge_id}</option>)}
                                                </select>
                                            ) : (
                                                todo.charge_name ? <span className="font-mono text-stone-300">{todo.charge_name}</span> : <span className="text-stone-500 italic">All</span>
                                            )}
                                        </td>

                                        {/* Task */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <Input value={editData.task} onChange={(e) => setEditData({ ...editData, task: e.target.value })} className="h-8 text-xs" />
                                            ) : (
                                                <span className="text-stone-200">{todo.task}</span>
                                            )}
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <select
                                                    value={editData.status}
                                                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                                                    className="bg-stone-800 border-stone-600 text-white text-xs rounded px-2 h-8 w-full focus:outline-none focus:border-blue-500"
                                                >
                                                    {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                                                </select>
                                            ) : (
                                                <Badge color={getStatusColor(todo.status)}>{todo.status}</Badge>
                                            )}
                                        </td>

                                        {/* Due Date */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <Input type="date" value={editData.due_date} onChange={(e) => setEditData({ ...editData, due_date: e.target.value })} className="h-8 text-xs" />
                                            ) : (
                                                todo.due_date ? <span className="text-stone-400">{new Date(todo.due_date).toLocaleDateString()}</span> : <span className="text-stone-600">-</span>
                                            )}
                                        </td>

                                        {/* Notes */}
                                        <td className="px-4 py-0.5">
                                            {isEditing ? (
                                                <Input value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} className="h-8 text-xs" />
                                            ) : (
                                                <span className="text-xs text-stone-500 truncate max-w-[150px] block" title={todo.notes}>{todo.notes}</span>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td className="px-4 py-0.5 text-right">
                                            <div className="flex justify-end gap-1">
                                                {isEditing ? (
                                                    <>
                                                        <button onClick={() => saveEditing(todo.id)} className="text-green-500 p-1 hover:bg-stone-800 rounded"><Check size={14} /></button>
                                                        <button onClick={() => setEditingId(null)} className="text-stone-500 p-1 hover:bg-stone-800 rounded"><X size={14} /></button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button onClick={() => startEditing(todo)} className="text-stone-500 hover:text-blue-400 p-1 hover:bg-stone-800 rounded"><Pencil size={14} /></button>
                                                        <button onClick={() => handleDeleteTodo(todo.id)} className="text-stone-500 hover:text-red-400 p-1 hover:bg-stone-800 rounded"><Trash size={14} /></button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <ToDoAddModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAdd={handleAddTodo}
                groups={groups}
                charges={charges}
                statuses={statuses}
            />

            <ToDoGroupManagementModal
                isOpen={isGroupModalOpen}
                onClose={() => setIsGroupModalOpen(false)}
                groups={groups}
                onAdd={handleAddGroup}
                onUpdate={handleUpdateGroup}
                onDelete={handleDeleteGroup}
            />

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                isDestructive={confirmModal.isDestructive}
            />
        </div>
    );
};
